import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { cx } from './cx';
import { useAnchoredStyle, useOutsidePress } from './popover';

export interface DatePickerProps {
  value: Date | null;
  onChange: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  /** Days that get the small accent marker dot (e.g. days with services). */
  hasMarker?: (date: Date) => boolean;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const sameDay = (a: Date | null | undefined, b: Date | null | undefined) =>
  !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const addMonths = (d: Date, n: number) => {
  const target = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const dim = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return new Date(target.getFullYear(), target.getMonth(), Math.min(d.getDate(), dim));
};
const keyOf = (d: Date) => `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;

/** "Wed, Nov 4, 2026" */
export const formatPickerDate = (d: Date) =>
  d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
const formatLong = (d: Date) =>
  d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
const formatMonth = (y: number, m: number) =>
  new Date(y, m, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

export function DatePicker({
  value,
  onChange,
  minDate,
  maxDate,
  hasMarker,
  placeholder = 'Choose a date',
  id,
  disabled,
  ...aria
}: DatePickerProps) {
  const autoId = useId().replace(/:/g, '');
  const baseId = id || `dp-${autoId}`;
  const titleId = `${baseId}-title`;
  const today = useMemo(() => startOfDay(new Date()), []);
  const min = minDate ? startOfDay(minDate) : null;
  const max = maxDate ? startOfDay(maxDate) : null;

  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState<Date>(() => startOfDay(value ?? today));
  const [view, setView] = useState(() => ({ y: (value ?? today).getFullYear(), m: (value ?? today).getMonth() }));
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const moveFocus = useRef(false);

  const outOfRange = useCallback((d: Date) => (min && d < min) || (max && d > max), [min, max]);

  const openPanel = () => {
    if (disabled) return;
    const start = startOfDay(value ?? (max && today > max ? max : today));
    setFocused(start);
    setView({ y: start.getFullYear(), m: start.getMonth() });
    moveFocus.current = true;
    setOpen(true);
  };
  const close = useCallback((refocus = false) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);

  useOutsidePress(open, [triggerRef, panelRef], () => close());
  const panelStyle = useAnchoredStyle(open, triggerRef, { maxHeight: 420, minWidth: 300 });

  // Roving focus: after keyboard moves (or opening), focus the focused day.
  useLayoutEffect(() => {
    if (!open || !moveFocus.current || !panelStyle) return;
    moveFocus.current = false;
    panelRef.current?.querySelector<HTMLButtonElement>(`[data-date="${keyOf(focused)}"]`)?.focus();
  }, [open, focused, view, panelStyle]);

  useEffect(() => {
    if (!open) moveFocus.current = false;
  }, [open]);

  const select = (d: Date) => {
    if (outOfRange(d)) return;
    onChange(startOfDay(d));
    close(true);
  };

  const goTo = (d: Date) => {
    setFocused(d);
    if (d.getFullYear() !== view.y || d.getMonth() !== view.m) setView({ y: d.getFullYear(), m: d.getMonth() });
    moveFocus.current = true;
  };

  const nav = (delta: number) => {
    const next = new Date(view.y, view.m + delta, 1);
    setView({ y: next.getFullYear(), m: next.getMonth() });
    setFocused(addMonths(focused, delta));
  };

  const onGridKey = (e: KeyboardEvent<HTMLDivElement>) => {
    let next: Date | null = null;
    switch (e.key) {
      case 'ArrowLeft': next = addDays(focused, -1); break;
      case 'ArrowRight': next = addDays(focused, 1); break;
      case 'ArrowUp': next = addDays(focused, -7); break;
      case 'ArrowDown': next = addDays(focused, 7); break;
      case 'PageUp': next = addMonths(focused, e.shiftKey ? -12 : -1); break;
      case 'PageDown': next = addMonths(focused, e.shiftKey ? 12 : 1); break;
      case 'Home': next = addDays(focused, -focused.getDay()); break;
      case 'End': next = addDays(focused, 6 - focused.getDay()); break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        select(focused);
        return;
      case 'Escape':
      case 'Tab':
        // The panel is portaled, so tabbing out of it would land outside any
        // enclosing modal; return to the trigger instead.
        e.preventDefault();
        e.stopPropagation();
        close(true);
        return;
      default:
        return;
    }
    e.preventDefault();
    goTo(next);
  };

  const onTriggerKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPanel();
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation();
      close();
    }
  };

  // Build the month grid, padded to whole weeks.
  const weeks = useMemo(() => {
    const first = new Date(view.y, view.m, 1);
    const dim = new Date(view.y, view.m + 1, 0).getDate();
    const cells: Array<Date | null> = Array.from({ length: first.getDay() }, () => null);
    for (let d = 1; d <= dim; d++) cells.push(new Date(view.y, view.m, d));
    while (cells.length % 7) cells.push(null);
    const rows: Array<Array<Date | null>> = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [view]);

  return (
    <div className="relative w-full min-w-0">
      <button
        ref={triggerRef}
        id={baseId}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={aria['aria-label'] ? `${aria['aria-label']}${value ? `, ${formatLong(value)}` : ''}` : undefined}
        aria-labelledby={aria['aria-labelledby'] ? `${aria['aria-labelledby']} ${baseId}` : undefined}
        disabled={disabled}
        data-open={open}
        onClick={() => (open ? close() : openPanel())}
        onKeyDown={onTriggerKey}
        className="wp-field h-[46px] px-3.5 flex items-center gap-2.5 text-left text-[14.5px] cursor-pointer hover:bg-fill2 disabled:opacity-50"
      >
        <Calendar size={16} strokeWidth={1.8} className="text-t4 shrink-0" aria-hidden="true" />
        <span className={cx('flex-1 min-w-0 truncate', !value && 'text-t5')}>{value ? formatPickerDate(value) : placeholder}</span>
      </button>
      {open &&
        panelStyle &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="false"
            aria-labelledby={titleId}
            style={{ ...panelStyle, width: 300 }}
            className="wp-pop bg-modal border border-line2 rounded-[18px] p-3.5 shadow-wp"
          >
            <div className="flex items-center justify-between mb-2.5">
              <button
                type="button"
                onClick={() => nav(-1)}
                aria-label="Previous month"
                className="w-8 h-8 rounded-full bg-fill hover:bg-fill2 text-t2 flex items-center justify-center"
              >
                <ChevronLeft size={16} aria-hidden="true" />
              </button>
              <span id={titleId} className="text-[14.5px] font-semibold" aria-live="polite">
                {formatMonth(view.y, view.m)}
              </span>
              <button
                type="button"
                onClick={() => nav(1)}
                aria-label="Next month"
                className="w-8 h-8 rounded-full bg-fill hover:bg-fill2 text-t2 flex items-center justify-center"
              >
                <ChevronRight size={16} aria-hidden="true" />
              </button>
            </div>
            <div role="grid" aria-labelledby={titleId} onKeyDown={onGridKey} className="flex flex-col gap-0.5">
              <div role="row" className="grid grid-cols-7 gap-0.5">
                {WEEKDAYS.map((w) => (
                  <span key={w} role="columnheader" aria-label={w} className="text-center text-[11px] font-semibold text-t4 py-1">
                    {w[0]}
                  </span>
                ))}
              </div>
              {weeks.map((row, ri) => (
                <div role="row" key={ri} className="grid grid-cols-7 gap-0.5">
                  {row.map((d, ci) => {
                    if (!d) return <span key={ci} role="gridcell" />;
                    const isSel = sameDay(d, value);
                    const isToday = sameDay(d, today);
                    const blocked = !!outOfRange(d);
                    const isFocus = sameDay(d, focused);
                    const marker = hasMarker?.(d);
                    return (
                      <span key={ci} role="gridcell" aria-selected={isSel}>
                        <button
                          type="button"
                          data-date={keyOf(d)}
                          tabIndex={isFocus ? 0 : -1}
                          aria-label={formatLong(d)}
                          aria-disabled={blocked || undefined}
                          aria-current={isToday ? 'date' : undefined}
                          onClick={() => select(d)}
                          onFocus={() => !isFocus && setFocused(d)}
                          className={cx(
                            'relative w-full h-9 rounded-[10px] border font-mono text-[13px] font-bold hover:outline hover:outline-1 hover:outline-line2',
                            isSel ? 'bg-acc text-white border-transparent' : 'bg-transparent',
                            !isSel && (blocked ? 'text-t5 cursor-not-allowed' : 'text-t1 cursor-pointer'),
                            !isSel && (isToday ? 'border-line2' : 'border-transparent'),
                          )}
                        >
                          {d.getDate()}
                          {marker && (
                            <span
                              aria-hidden="true"
                              className="absolute left-1/2 bottom-1 w-1 h-1 -ml-0.5 rounded-full"
                              style={{ background: isSel ? '#fff' : 'var(--acc)' }}
                            />
                          )}
                        </button>
                      </span>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
